/**
 * Agent Avatar Service - React Query Integration
 * Handles all avatar-related API communications with /v1/admin/agent-avatars
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type {
  AgentAvatar,
  ListAvatarsResponse,
  AvatarResponse,
  ListAvatarsParams,
  UploadAvatarPayload,
  UpdateAvatarPayload,
  ToggleAvatarStatusPayload,
} from '../features/admin/agent-avatars/types';

// ============================================
// Query Keys
// ============================================

export const avatarKeys = {
  all: ['admin', 'agent-avatars'] as const,
  lists: () => [...avatarKeys.all, 'list'] as const,
  list: (params: ListAvatarsParams) => [...avatarKeys.lists(), params] as const,
  details: () => [...avatarKeys.all, 'detail'] as const,
  detail: (id: string) => [...avatarKeys.details(), id] as const,
};

// ============================================
// API Functions
// ============================================

const BASE = '/v1/admin/agent-avatars';

/**
 * Fetch paginated list of avatars with optional filters
 */
const fetchAvatars = async (params: ListAvatarsParams): Promise<ListAvatarsResponse> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', String(params.page));
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.status && params.status !== 'all') queryParams.set('status', params.status);
  if (params.category) queryParams.set('category', params.category);
  if (params.type) queryParams.set('type', params.type);

  const response = await axiosInstance.get<ListAvatarsResponse>(
    `${BASE}?${queryParams.toString()}`
  );
  return response.data;
};

/**
 * Fetch a single avatar by publicId
 */
const fetchAvatarById = async (publicId: string): Promise<AgentAvatar> => {
  const response = await axiosInstance.get<AvatarResponse>(`${BASE}/${publicId}`);
  return response.data.data;
};

/**
 * Upload a new avatar (multipart/form-data)
 */
const uploadAvatar = async (payload: UploadAvatarPayload): Promise<AgentAvatar> => {
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('name', payload.name);
  if (payload.category) formData.append('category', payload.category);
  if (payload.tags) formData.append('tags', payload.tags);
  if (payload.isDefault !== undefined) formData.append('isDefault', String(payload.isDefault));

  const response = await axiosInstance.post<AvatarResponse>(BASE, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

/**
 * Update avatar metadata (name, category, tags, isDefault, previewUrl)
 */
const updateAvatar = async ({ publicId, ...body }: UpdateAvatarPayload): Promise<AgentAvatar> => {
  const response = await axiosInstance.patch<AvatarResponse>(`${BASE}/${publicId}`, body);
  return response.data.data;
};

/**
 * Toggle avatar isActive status
 */
const toggleAvatarStatus = async ({
  publicId,
  isActive,
}: ToggleAvatarStatusPayload): Promise<AgentAvatar> => {
  const response = await axiosInstance.patch<AvatarResponse>(`${BASE}/${publicId}/status`, {
    isActive,
  });
  return response.data.data;
};

/**
 * Soft-delete an avatar (sets isActive = false)
 */
const deleteAvatar = async (publicId: string): Promise<AgentAvatar> => {
  const response = await axiosInstance.delete<AvatarResponse>(`${BASE}/${publicId}`);
  return response.data.data;
};

// ============================================
// React Query Hooks
// ============================================

/**
 * Hook to fetch paginated avatar list with filters
 */
export const useAvatars = (params: ListAvatarsParams = {}) => {
  return useQuery({
    queryKey: avatarKeys.list(params),
    queryFn: () => fetchAvatars(params),
    staleTime: 30_000,
  });
};

/**
 * Hook to fetch a single avatar by publicId
 */
export const useAvatarById = (publicId: string) => {
  return useQuery({
    queryKey: avatarKeys.detail(publicId),
    queryFn: () => fetchAvatarById(publicId),
    enabled: Boolean(publicId),
    staleTime: 30_000,
  });
};

/**
 * Hook to upload a new avatar
 */
export const useUploadAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadAvatar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: avatarKeys.lists() });
    },
  });
};

/**
 * Hook to update avatar metadata
 */
export const useUpdateAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAvatar,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: avatarKeys.lists() });
      queryClient.invalidateQueries({ queryKey: avatarKeys.detail(data.publicId) });
    },
  });
};

/**
 * Hook to toggle avatar active/inactive status
 */
export const useToggleAvatarStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleAvatarStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: avatarKeys.lists() });
    },
  });
};

/**
 * Hook to permanently delete an avatar
 */
export const useDeleteAvatar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAvatar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: avatarKeys.lists() });
    },
  });
};
